/**
 * A compact trend of the most recent sessions, drawn as a line with one point per session.
 *
 * It answers "how does this session compare to the last few?" while the roll call is being
 * taken. A line is used rather than bars because the vertical range is fitted to the values
 * on screen: bars must be read from a zero baseline, a line need not be, and fitting the
 * range is what makes a run of rates between 70% and 95% legible at this size. The fitted
 * range is printed at the end of the strip, so the scale is never left implied.
 *
 * It sits above a roll call in progress, where every line of prose costs a roster row, so it
 * is labelled rather than captioned: the sentence a caption would carry is on the chart's own
 * accessible name instead.
 *
 * Every point is also a control that jumps to its session, so the strip doubles as navigation.
 */

import { useMemo, type ReactNode } from 'react';

import { formatRate, type SessionTally } from '../../../shared/stats.js';
import type { IsoDate } from '../../../shared/types.js';
import { formatMediumDate } from '../format.js';
import { useMeasuredWidth } from './measure.js';

/** Drawing height of the line, in pixels. */
const PLOT_HEIGHT = 34;

/** Vertical breathing room above and below the fitted range, in pixels. */
const VERTICAL_INSET = 5;

/** Smallest span the fitted range may collapse to, so a flat run does not sit on one line. */
const MIN_RANGE = 0.08;

/** Radius of an ordinary point, and of the selected one. */
const POINT_RADIUS = { normal: 3, selected: 5 } as const;

/** A session that has a rate, with its drawn position. */
interface Point {
  tally: SessionTally;
  rate: number;
  x: number;
  y: number;
}

/**
 * Renders the strip, or nothing when there are too few recorded sessions to compare.
 *
 * Deciding that here rather than at the call site keeps one rule in one place: a caller that
 * guessed at the threshold would draw an empty band whenever the two disagreed.
 */
export function SessionSpark({
  tallies,
  selected,
  onSelect,
}: {
  /** Sessions in ascending date order. */
  tallies: readonly SessionTally[];
  /** Date currently open in the roll call, highlighted in the strip. */
  selected: IsoDate;
  onSelect: (date: IsoDate) => void;
}): ReactNode {
  const [container, width] = useMeasuredWidth<HTMLDivElement>();

  const recorded = useMemo(() => tallies.filter((tally) => tally.rate !== null), [tallies]);

  const { points, low, high } = useMemo(() => {
    if (recorded.length === 0) return { points: [] as Point[], low: 0, high: 1 };
    const rates = recorded.map((tally) => tally.rate ?? 0);
    const observedLow = Math.min(...rates);
    const observedHigh = Math.max(...rates);
    const centre = (observedLow + observedHigh) / 2;
    const span = Math.max(observedHigh - observedLow, MIN_RANGE);
    const bottom = Math.max(0, Math.min(observedLow, centre - span / 2));
    const top = Math.min(1, Math.max(observedHigh, centre + span / 2));
    const usableHeight = PLOT_HEIGHT - VERTICAL_INSET * 2;
    const step = recorded.length <= 1 ? 0 : (width - POINT_RADIUS.selected * 2) / (recorded.length - 1);

    return {
      low: bottom,
      high: top,
      points: recorded.map((tally, index): Point => {
        const rate = tally.rate ?? 0;
        const share = top === bottom ? 0.5 : (rate - bottom) / (top - bottom);
        return {
          tally,
          rate,
          x: POINT_RADIUS.selected + (recorded.length === 1 ? (width - POINT_RADIUS.selected * 2) / 2 : index * step),
          y: VERTICAL_INSET + (1 - share) * usableHeight,
        };
      }),
    };
  }, [recorded, width]);

  if (recorded.length < 3) return null;

  const path = points
    .map((point, index) => `${index === 0 ? 'M' : 'L'}${point.x.toFixed(1)} ${point.y.toFixed(1)}`)
    .join(' ');

  return (
    // The rule belongs to the strip, so a project without enough recorded sessions to compare
    // renders nothing at all rather than an empty ruled band.
    <div className="mt-4 flex items-center gap-3 border-t border-line pt-3">
      <div ref={container} className="min-w-0 flex-1">
      <div className="relative" style={{ height: PLOT_HEIGHT }}>
        <svg
          width={width}
          height={PLOT_HEIGHT}
          role="img"
          aria-label={`Present rate over the last ${points.length} sessions, between ${formatRate(low)}`
            + ` and ${formatRate(high)}. Each point opens that session.`}
        >
          <path d={path} fill="none" stroke="var(--color-brand-graphic)" strokeWidth={2} strokeLinejoin="round" />
          {points.map((point) => {
            const isSelected = point.tally.date === selected;
            return (
              <circle
                key={point.tally.date}
                cx={point.x}
                cy={point.y}
                r={isSelected ? POINT_RADIUS.selected : POINT_RADIUS.normal}
                fill={isSelected ? 'var(--color-accent)' : 'var(--color-brand-graphic)'}
                stroke="var(--color-surface)"
                strokeWidth={isSelected ? 2 : 1}
              />
            );
          })}
        </svg>

        {/* Full-height hit areas, so a point that sits near the top is still easy to tap. */}
        <div className="absolute inset-0 flex">
          {points.map((point) => (
            <button
              key={point.tally.date}
              type="button"
              onClick={() => onSelect(point.tally.date)}
              aria-label={`${formatMediumDate(point.tally.date)}, ${formatRate(point.rate)} present`}
              aria-current={point.tally.date === selected ? 'true' : undefined}
              title={`${formatMediumDate(point.tally.date)} · ${formatRate(point.rate)}`}
              className="h-full flex-1"
            />
          ))}
        </div>
      </div>
      </div>
      {/* The line is fitted to the values on screen rather than to zero, so the range it was
          fitted to is printed where the line ends. */}
      <p className="tabular shrink-0 text-right text-tiny leading-tight text-ink-3">
        {formatRate(high)}
        <span className="block">{formatRate(low)}</span>
      </p>
    </div>
  );
}
